/**
 * utmTracker.js
 * Captures UTM parameters from the URL on first visit and:
 *  1. Saves them to sessionStorage for use during registration.
 *  2. Records a single impression by calling the track_utm Cloud Function.
 *     The impression is fired only once per browser session (idempotent).
 */

const SESSION_FIRED_KEY = "utm_impression_fired";
const UTM_PARAMS_KEY = "utm_params";

const TRACK_UTM_URL = import.meta.env.VITE_TRACK_UTM_URL;

/**
 * Call on app startup. Reads utm_source from the URL, saves it to
 * sessionStorage, and fires a single impression event (fire-and-forget).
 */
export function captureAndTrackUtm() {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  if (!utmSource) return;

  const utmData = {
    utm_source: utmSource,
    utm_medium: params.get("utm_medium") || "-",
    utm_campaign: params.get("utm_campaign") || "-",
  };

  // Always save/overwrite so the latest UTM params are available during registration
  sessionStorage.setItem(UTM_PARAMS_KEY, JSON.stringify(utmData));

  // Only fire the impression once per session — avoid double-counting on refresh
  if (sessionStorage.getItem(SESSION_FIRED_KEY)) return;
  sessionStorage.setItem(SESSION_FIRED_KEY, "1");

  if (!TRACK_UTM_URL) return;

  // Fire-and-forget — errors are non-critical
  fetch(TRACK_UTM_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(utmData),
  }).catch(() => {});
}

/**
 * Returns the UTM params stored in sessionStorage, or null if none.
 * Used during registration to include utm_source in the user document.
 */
export function getStoredUtmParams() {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_PARAMS_KEY)) || null;
  } catch {
    return null;
  }
}
